import { twMerge } from 'tailwind-merge';

import { Icon, IconName } from '~/modules/elements/akui/icon';

export type SongListTab = 'all' | 'saved' | 'queue';

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  tabs: Array<{ tab: SongListTab; label: string; count: number; test: string }>;
  tab: SongListTab;
  onTabChange: (tab: SongListTab) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  // How many languages are shown, when some are hidden.
  shownLanguages?: number;
  onOpenLanguages: () => void;
}

// The search, the two filter buttons and the three lists stay on every tab, so a song can be found
// and queued from the Queue tab without leaving it first.
export default function SongListHeader({
  search,
  onSearchChange,
  tabs,
  tab,
  onTabChange,
  activeFilterCount,
  onOpenFilters,
  shownLanguages,
  onOpenLanguages,
}: Props) {
  const typing = search !== '';

  return (
    <div className="flex shrink-0 flex-col gap-3 bg-black/60 px-5 pt-3.5 pb-3">
      <div className="flex gap-2">
        <div
          className={twMerge(
            'flex h-12 min-w-0 flex-1 items-center gap-2.5 rounded-2xl bg-white/8 pr-3 pl-3.5 transition-shadow duration-150',
            typing ? 'shadow-[inset_0_0_0_2px_#facc15]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]',
          )}>
          <Icon icon="ic:baseline-search" size={5.5} className="shrink-0 text-white/60" />
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onSearchChange('');
            }}
            placeholder="Songs, artists, genres…"
            data-test="search-input"
            className="h-full min-w-0 flex-1 border-none bg-transparent text-[1.0625rem] text-white outline-none placeholder:text-white/45"
          />
          {typing && (
            <button
              type="button"
              aria-label="Clear search"
              onMouseDown={(event) => {
                // Ahead of the input's blur, which would otherwise swallow the click.
                event.preventDefault();
                onSearchChange('');
              }}
              data-test="search-close-button"
              className="flex shrink-0 cursor-pointer border-none bg-transparent p-0 text-white">
              <Icon icon="ic:baseline-close" size={5} />
            </button>
          )}
        </div>
        <BadgedSquare
          icon="ic:baseline-filter-list"
          label="Filter songs"
          test="filters-button"
          onClick={onOpenFilters}
          badge={activeFilterCount > 0 ? activeFilterCount : undefined}
        />
        <BadgedSquare
          icon="ic:baseline-language"
          label="Languages"
          test="song-language-filter"
          onClick={onOpenLanguages}
          badge={shownLanguages}
        />
      </div>
      <div className="flex h-11 gap-1 rounded-full bg-black/50 p-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]">
        {tabs.map((entry) => {
          const active = entry.tab === tab;

          return (
            <button
              key={entry.tab}
              type="button"
              onClick={() => onTabChange(entry.tab)}
              data-test={entry.test}
              data-active={active}
              className={twMerge(
                'ph-no-capture min-w-0 flex-1 cursor-pointer truncate rounded-full border-none px-1.5 text-[0.8125rem] font-bold transition-[background-color,color] duration-200',
                active ? 'bg-active text-black' : 'bg-transparent text-white',
              )}>
              {entry.label} · {entry.count}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BadgedSquare({
  icon,
  label,
  test,
  onClick,
  badge,
}: {
  icon: IconName;
  label: string;
  test: string;
  onClick: () => void;
  badge?: number;
}) {
  const active = badge !== undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-test={test}
      data-active={active}
      className={twMerge(
        'relative flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-2xl border-none bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] transition-transform duration-100 active:scale-[0.94]',
        active ? 'text-active' : 'text-white',
      )}>
      <Icon icon={icon} size={5.5} />
      {active && (
        <span className="bg-active absolute -top-1 -right-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1 text-[0.6875rem] font-bold text-black">
          {badge}
        </span>
      )}
    </button>
  );
}
